import React from 'react';
import { Prompt } from 'react-router';

import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type * as H from 'history';

import { Form } from 'bz-core-controls/controls/form/field/generics';
import type { IProps as IFormProps } from 'bz-core-controls/controls/form/field/generics/Form';

import history from 'bz-utilities/modules/networking/history/rr4';

import { Button } from 'click-controls';
import { TOASTIFY_TYPE } from 'click-controls/constant/enums';
import { TOASTIFY_MSG } from 'click-controls/constant/message';
import Dialog from 'click-controls/controls/dialog';
import ToastifyStore from 'click-controls/controls/toastify/toastify-store';

interface IProps<T> extends IFormProps<T> {}

@observer
class ClickForm<T> extends React.Component<IProps<T>> {
  @observable isOpenDialog: boolean = false;
  @observable isAcceptLeave: boolean = false;
  @observable path: string = '';

  render() {
    const { className, store, wrapInForm = true } = this.props;
    return (
      <Form className={className} store={store} wrapInForm={wrapInForm}>
        {this.props.children}
        <Dialog
          bodyClass="text-gray-600"
          footerClass="border-none justify-center"
          headerClass="border-none font-bold text-xl text-gray-600"
          btnCancelClass="btn-primary"
          isOpen={this.isOpenDialog}
          title="Unsaved Changes"
          centered={true}
          closable={true}
          isDismissable={false}
          okText="Confirm"
          width="[600px]"
          cancelText="Cancel"
          onClose={this.onCloseDialog}
          footer={
            <div className="flex flex-wrap justify-end gap-3 pb-2 pt-6">
              <Button className="btn xl:w-auto" onPress={this.onCloseDialog}>
                Cancel
              </Button>
              <Button className="btn xl:w-auto" onPress={this.onDontSave}>
                Don't Save
              </Button>
              <Button className="btn xl:w-auto" onPress={this.onSave}>
                Save
              </Button>
            </div>
          }
        >
          <div className="pt-6">
            <p>
              You have made changes on this screen without saving them. If you leave this screen
              without saving, your changes will be lost.
            </p>
            <p className="pt-6">Do you want to leave without saving?</p>
          </div>
        </Dialog>
        {!store.isSaving && <Prompt when={store.hasChanges} message={this.onMessage} />}
      </Form>
    );
  }

  @action.bound
  openDialog() {
    this.isOpenDialog = true;
  }

  @action.bound
  onDontSave() {
    this.isOpenDialog = false;
    this.isAcceptLeave = true;
    history.push(this.path);
  }

  @action.bound
  onCloseDialog() {
    this.isOpenDialog = false;
  }

  @action.bound
  async onSave(): Promise<void> {
    const accept = await this._save();

    runInAction(() => {
      this.isAcceptLeave = accept;

      this.isOpenDialog = false;
    });
    if (accept && this.path) history.replace(this.path);
  }

  @action.bound
  protected async _save(): Promise<boolean> {
    ToastifyStore.setMessage(TOASTIFY_TYPE.Info, TOASTIFY_MSG.SAVING);

    const { save } = this.props.store;

    const success = await save();

    if (success) {
      ToastifyStore.setMessage(TOASTIFY_TYPE.Success, TOASTIFY_MSG.SAVED);
    } else {
      ToastifyStore.setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.ERROR);
    }

    return success;
  }
  @action.bound
  onMessage(location: H.location, action: H.Action): string | boolean {
    const { hasChanges } = this.props.store;
    if (!hasChanges) {
      this.path = location.pathname;
      return this.isAcceptLeave;
    }
    if (!!localStorage.getItem('isAuthenticated') && hasChanges) {
      this.openDialog();
      this.path = location.pathname;
      return this.isAcceptLeave;
    }
    return false;
  }
}

export default ClickForm;
